import type { Tournament, Round } from '../types';

interface SchedulePageProps {
  tournament: Tournament;
}

export default function SchedulePage({ tournament }: SchedulePageProps) {
  const getPlayerName = (id: string) => {
    const player = tournament.players.find(p => p.id === id);
    return player?.name || 'Unknown';
  };

  const getSkillBadge = (id: string) => {
    const player = tournament.players.find(p => p.id === id);
    if (!player) return null;
    
    const classes = {
      1: 'badge-beginner',
      2: 'badge-intermediate',
      3: 'badge-advanced'
    };
    
    const labels = {
      1: 'B',
      2: 'I',
      3: 'A'
    };
    
    return <span className={`badge ${classes[player.skill]}`} style={{ marginLeft: '0.25rem', fontSize: '0.75rem' }}>{labels[player.skill]}</span>;
  };

  const getRoundProgress = (round: Round) => {
    const completed = round.matches.filter(m => m.isComplete).length;
    return `${completed}/${round.matches.length}`;
  };

  // Calculate overall progress
  const totalCompleted = tournament.schedule.reduce((acc, round) => 
    acc + round.matches.filter(m => m.isComplete).length, 0
  );
  const totalMatches = tournament.schedule.reduce((acc, round) => 
    acc + round.matches.length, 0
  );

  return (
    <div>
      <h1>Tournament Schedule</h1>
      
      <div className="card mb-2">
        <h3>{tournament.config.name}</h3>
        <p>
          <strong>Format:</strong> {tournament.config.format === 'rotating' ? 'Rotating Partner Round Robin' : 
            tournament.config.format === 'balanced' ? 'Balanced (Strict Skill Matching)' : 
            tournament.config.format === 'swiss' ? 'Swiss Format' : 'Fixed Team Round Robin'}
          <br />
          <strong>Courts:</strong> {tournament.config.courts} | 
          <strong>Rounds:</strong> {tournament.config.rounds} | 
          <strong>Target Score:</strong> {tournament.config.targetScore} (win by {tournament.config.winBy})
          <br />
          <strong>Players:</strong> {tournament.players.length}
        </p>
        
        {/* Overall Progress Bar */}
        <div style={{ marginTop: '1rem' }}>
          <div style={{ marginBottom: '0.5rem' }}>
            <strong>Overall Progress:</strong> {totalCompleted}/{totalMatches} matches completed
          </div>
          <div style={{ 
            background: '#e0e0e0', 
            borderRadius: '4px', 
            height: '20px',
            overflow: 'hidden'
          }}>
            <div style={{
              background: totalCompleted === totalMatches ? '#27ae60' : '#3498db',
              height: '100%',
              width: `${totalMatches > 0 ? (totalCompleted / totalMatches) * 100 : 0}%`,
              transition: 'width 0.3s'
            }} />
          </div>
        </div>
      </div>

      {/* All Rounds - Scrollable List */}
      <div className="all-rounds">
        {tournament.schedule.map((round) => (
          <div key={round.index} className="card mb-2 round-section">
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center', 
              marginBottom: '1rem', 
              flexWrap: 'wrap', 
              gap: '0.5rem',
              padding: '0.75rem',
              background: '#f8f9fa',
              borderRadius: '4px',
              borderLeft: '4px solid #3498db'
            }}>
              <h2 style={{ margin: 0, fontSize: '1.25rem' }}>Round {round.index + 1}</h2>
              <span className="badge" style={{ 
                background: round.matches.every(m => m.isComplete) ? '#d4edda' : '#fff3cd',
                color: round.matches.every(m => m.isComplete) ? '#155724' : '#856404'
              }}>
                {getRoundProgress(round)} Complete
              </span>
            </div>
            
            {round.sittingOutPlayerIds.length > 0 && (
              <div className="mb-2" style={{ background: '#fff3cd', padding: '0.75rem', borderRadius: '4px' }}>
                <strong>Sitting Out:</strong>{' '}
                {round.sittingOutPlayerIds.map(id => getPlayerName(id)).join(', ')}
              </div>
            )}

            <div className="matches-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1rem' }}>
              {round.matches.map((match) => (
                <div key={match.id} className="card match-card" style={{ 
                  background: match.isComplete ? '#d4edda' : 'white',
                  padding: '1rem',
                  marginBottom: 0
                }}>
                  <div className="flex flex-between mb-1">
                    <strong>Court {match.courtIndex + 1}</strong>
                    {match.isComplete && (
                      <span className="badge" style={{ background: '#28a745', color: 'white' }}>
                        Complete
                      </span>
                    )}
                  </div>

                  <div className="match-teams" style={{ 
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '0.75rem'
                  }}>
                    {/* Team A */}
                    <div className="team team-a" style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'space-between',
                      padding: '0.5rem',
                      background: 'rgba(52, 152, 219, 0.1)',
                      borderRadius: '4px'
                    }}>
                      <div>
                        <div style={{ fontWeight: 500 }}>
                          {getPlayerName(match.teamA.player1Id)}
                          {getSkillBadge(match.teamA.player1Id)}
                        </div>
                        <div style={{ fontWeight: 500 }}>
                          {getPlayerName(match.teamA.player2Id)}
                          {getSkillBadge(match.teamA.player2Id)}
                        </div>
                      </div>
                      {match.isComplete && (
                        <span style={{ fontSize: '1.25rem', fontWeight: 'bold' }}>{match.teamAScore}</span>
                      )}
                    </div>

                    {/* VS */}
                    <div style={{ textAlign: 'center', fontWeight: 'bold', color: '#666' }}>
                      {match.isComplete ? 'vs' : 'VS'}
                    </div>

                    {/* Team B */}
                    <div className="team team-b" style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'space-between',
                      padding: '0.5rem',
                      background: 'rgba(231, 76, 60, 0.1)',
                      borderRadius: '4px'
                    }}>
                      <div>
                        <div style={{ fontWeight: 500 }}>
                          {getPlayerName(match.teamB.player1Id)}
                          {getSkillBadge(match.teamB.player1Id)}
                        </div>
                        <div style={{ fontWeight: 500 }}>
                          {getPlayerName(match.teamB.player2Id)}
                          {getSkillBadge(match.teamB.player2Id)}
                        </div>
                      </div>
                      {match.isComplete && (
                        <span style={{ fontSize: '1.25rem', fontWeight: 'bold' }}>{match.teamBScore}</span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {round.matches.length === 0 && (
              <p className="text-center">No matches scheduled for this round.</p>
            )}
          </div>
        ))}
      </div>

      {/* Scroll to top button */}
      <button
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        style={{
          position: 'fixed',
          bottom: '20px',
          right: '20px',
          width: '50px',
          height: '50px',
          borderRadius: '50%',
          background: '#3498db',
          color: 'white',
          border: 'none',
          cursor: 'pointer',
          boxShadow: '0 2px 10px rgba(0,0,0,0.2)',
          fontSize: '1.5rem',
          zIndex: 999
        }}
        title="Scroll to top"
      >
        ↑
      </button>
    </div>
  );
}
